import { inject, Injectable } from "@angular/core";

import { LoggerService } from "./logger.service";

@Injectable({
  providedIn: "root"
})
export class UserPreferencesService {
  private readonly _documentChatCollapsedKey = "document-chat-collapsed";
  private readonly _documentSidebarCollapsedKey = "document-sidebar-collapsed";
  private readonly _sidebarTabKey = "sidebar-tab";
  private readonly _documentFilterKey = "document-filter";
  private readonly _isNewUserKey = "isNewUser";
  private readonly _recoveryTokenKey = "auth_recovery_token";
  private readonly _tableStatesKey = "tableStates";
  private readonly _logger = inject(LoggerService);

  // Generic helpers for resizable directive to persist vertical sizing
  getStoredHeight(key: string, def: number, min: number, max: number): number {
    try {
      const raw = localStorage.getItem(key);
      const val = raw ? Number(raw) : def;
      return Math.max(min, Math.min(val, max));
    } catch {
      return def;
    }
  }

  saveHeight(key: string, height: number, min: number, max: number): void {
    try {
      const valid = Math.max(min, Math.min(height, max));
      localStorage.setItem(key, String(valid));
    } catch {}
  }

  setRecoveryToken(token: string): void {
    localStorage.setItem(this._recoveryTokenKey, token);
  }

  getRecoveryToken(): string | null {
    return localStorage.getItem(this._recoveryTokenKey);
  }

  clearRecoveryToken(): void {
    localStorage.removeItem(this._recoveryTokenKey);
  }

  /**
   * Generic method to get a stored width for any resizable element
   * @param key The storage key for the element
   * @param defaultWidth Default width to use if no stored value exists
   * @param minWidth Minimum allowed width
   * @param maxWidth Maximum allowed width
   */
  getStoredWidth(key: string, defaultWidth: number, minWidth: number, maxWidth: number): number {
    const storedValue = localStorage.getItem(key);
    if (!storedValue) {
      return defaultWidth;
    }

    const parsedWidth = Number(storedValue);
    if (isNaN(parsedWidth)) {
      return defaultWidth;
    }

    // Ensure retrieved value is valid
    return Math.max(minWidth, Math.min(parsedWidth, maxWidth));
  }

  /**
   * Generic method to save a width for any resizable element
   * @param key The storage key for the element
   * @param width Width to store
   * @param minWidth Minimum allowed width
   * @param maxWidth Maximum allowed width
   */
  saveWidth(key: string, width: number, minWidth: number, maxWidth: number): void {
    // Ensure width is within the allowed range
    const validWidth = Math.max(minWidth, Math.min(width, maxWidth));
    localStorage.setItem(key, validWidth.toString());
  }

  getDocumentChatCollapsed(): boolean {
    return localStorage.getItem(this._documentChatCollapsedKey) === "true";
  }

  setDocumentChatCollapsed(collapsed: boolean): void {
    localStorage.setItem(this._documentChatCollapsedKey, collapsed.toString());
  }

  getDocumentSidebarCollapsed(): boolean {
    return localStorage.getItem(this._documentSidebarCollapsedKey) === "true";
  }

  setDocumentSidebarCollapsed(collapsed: boolean): void {
    localStorage.setItem(this._documentSidebarCollapsedKey, collapsed.toString());
  }

  setSidebarTab(tabId: string): void {
    localStorage.setItem(this._sidebarTabKey, tabId);
  }

  getSidebarTab(): string | null {
    return localStorage.getItem(this._sidebarTabKey);
  }

  setIsNewUser(isNewUser: boolean): void {
    localStorage.setItem(this._isNewUserKey, isNewUser.toString());
  }

  getIsNewUser(): boolean {
    return localStorage.getItem(this._isNewUserKey) === "true";
  }

  clearIsNewUser(): void {
    localStorage.removeItem(this._isNewUserKey);
  }

  getTableState(tableId: string): Record<string, any> | null {
    try {
      const raw = localStorage.getItem(this._tableStatesKey);
      const states = raw ? JSON.parse(raw) : {};
      return states[tableId] || null;
    } catch {
      return null;
    }
  }

  saveTableState(tableId: string, partialState: Record<string, any>): void {
    try {
      const raw = localStorage.getItem(this._tableStatesKey);
      const states = raw ? JSON.parse(raw) : {};
      const currentState = states[tableId] || {};

      states[tableId] = { ...currentState, ...partialState };

      localStorage.setItem(this._tableStatesKey, JSON.stringify(states));
    } catch (e) {
      this._logger.error("Failed to save table state", e);
    }
  }

  // Generic helpers (use instead of direct localStorage access elsewhere)
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      this._logger.warn("Failed to read user preference", { key, error: e });
      return null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      this._logger.warn("Failed to persist user preference", { key, error: e });
    }
  }

  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      this._logger.warn("Failed to remove user preference", { key, error: e });
    }
  }

  getJson<T>(key: string): T | null {
    try {
      const raw = this.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw) as T;
    } catch (e) {
      this._logger.warn("Failed to parse user preference JSON", { key, error: e });
      return null;
    }
  }

  setJson(key: string, value: unknown): void {
    try {
      this.setItem(key, JSON.stringify(value));
    } catch (e) {
      this._logger.warn("Failed to serialize user preference JSON", { key, error: e });
    }
  }
  setDocumentFilter(filter: string): void {
    localStorage.setItem(this._documentFilterKey, filter);
  }

  getDocumentFilter(): string | null {
    return localStorage.getItem(this._documentFilterKey);
  }
}
